import moment from "moment";

import {
  getDefaultBoardGroupBy,
  getDefaultListGroupBy,
  UNGROUPED_PSEUDO_GROUP_BY,
  UNSET_PSEUDO_SUBGROUP_BY,
} from "~/common/groupBy";
import { UNCOLORED_PSEUDO_COLOR_BY } from "~/constants/pseudoDuids";
import { isDefined } from "~/shared/common";
import {
  DashboardKind,
  Entitlement,
  FilterConnector,
  IconKind,
  LayoutKind,
  PageKind,
  StoreDataPreservationMode,
  Subscription,
  SubtaskDisplayMode,
  SummaryStatisticKind,
  UserRole,
  ViewKind,
} from "~/shared/enums";
import type {
  DartboardUpdate,
  Dashboard,
  DashboardUpdate,
  Filter,
  FilterGroup,
  FolderUpdate,
  Layout,
  LayoutConfig,
  LayoutUpdate,
  Page,
  PageUpdate,
  TransactionResponse,
  UserDartboardLayout,
  UserDartboardLayoutCreate,
  View,
  ViewUpdate,
} from "~/shared/types";
import { makeRandomColorHex } from "~/utils/color";
import { deepCopy, filterInPlace, getNextTitleInSequence, isString, makeDuid, randomSample } from "~/utils/common";
import { dashboardComparator, layoutComparator, viewComparator } from "~/utils/comparator";
import { getOrdersBetween } from "~/utils/orderManager";

import type { PiniaActionAdaptor, PiniaGetterAdaptor } from "../shared";
import type { DataStore } from ".";
import { removeFromSorted, setObjectMapAndSorted, updateInSorted } from "./utils";

export type State = {
  _duidsToDashboards: Map<string, Dashboard>;
  _sortedDashboardDuids: string[];
  _duidsToLayouts: Map<string, Layout>;
  _sortedLayoutDuids: string[];
  _duidsToViews: Map<string, View>;
  _sortedViewDuids: string[];
};

export const state: State = {
  _duidsToDashboards: new Map(),
  _sortedDashboardDuids: [],
  _duidsToLayouts: new Map(),
  _sortedLayoutDuids: [],
  _duidsToViews: new Map(),
  _sortedViewDuids: [],
};

export type Getters = {
  viewList: View[];
  getViewByDuid: (duid: string) => View | undefined;
  getViewByKind: (kind: ViewKind.SEARCH | ViewKind.MY_TASKS | ViewKind.TRASH) => View;
  layoutList: Layout[];
  getLayoutByDuid: (duid: string) => Layout | undefined;
  getLayoutByDartboardDuid: (dartboardDuid: string) => Layout | undefined;
  dashboardList: Dashboard[];
  getDashboardByDuid: (duid: string) => Dashboard | undefined;
};

export type Actions = {
  createView: (
    order: string,
    partialView?: Partial<View>,
    partialLayout?: Partial<Layout>,
    options?: { awaitBackend?: boolean }
  ) => Promise<View>;
  updateView: (
    update: ViewUpdate,
    options?: { awaitBackend?: boolean; noBackend?: boolean }
  ) => Promise<View | undefined>;
  updateViewAccessors: (
    viewDuid: string,
    accessorDuids: string[],
    add?: boolean,
    options?: { awaitBackend?: boolean }
  ) => Promise<View | undefined>;
  updateViewFavorite: (
    viewDuid: string,
    favorite: boolean,
    options?: { awaitBackend?: boolean }
  ) => Promise<View | undefined>;
  replicateView: (
    view: View,
    order: string,
    partialView?: Partial<View>,
    options?: { awaitBackend?: boolean; maintainTitle?: boolean }
  ) => Promise<View | undefined>;
  deleteView: (view: View, options?: { awaitBackend?: boolean }) => Promise<void>;
  $setViews: (views: View[]) => void;
  $createOrUpdateView: (view: View) => void;
  $deleteView: (view: View) => void;
  updatePage: (update: PageUpdate, pageKind: PageKind, options?: { awaitBackend?: boolean }) => Promise<void>;
  updatePageAccessors: (
    page: Page,
    accessorDuids: string[],
    add?: boolean,
    options?: { awaitBackend?: boolean }
  ) => Promise<void>;
  replicatePage: (page: Page, options?: { awaitBackend?: boolean }) => Promise<void>;
  deletePage: (page: Page, options?: { awaitBackend?: boolean }) => Promise<void>;
  makeLayoutNoBackend: (partialLayout?: Partial<Layout>) => Layout;
  createLayoutForDartboard: (
    dartboardDuid: string,
    filters: Filter[],
    options?: { awaitBackend?: boolean }
  ) => Promise<Layout | undefined>;
  updateLayout: (
    update: LayoutUpdate,
    options?: { noBackend?: boolean; awaitBackend?: boolean }
  ) => Promise<Layout | undefined>;
  /* Remove entity duids from filters in all layouts, or remove all filters if removeAllFilters is true. */
  $cleanupLayoutsFiltersNoBackend: (
    entityDuids: string[],
    propertyDuids: string[],
    options?: { removeAllFilters?: boolean }
  ) => void;
  /* Reset groupBy/colorBy/subgroupBy to defaults if referencing deleted property. */
  $cleanupLayoutsKindConfigMapNoBackend: (propertyDuids: string[]) => void;
  /* Remove property references from views. */
  $cleanupViewsPropertyReferencesNoBackend: (propertyDuids: string[]) => void;
  /* Remove deleted properties from layout sorts. */
  $cleanupLayoutsPropertySortsNoBackend: (propertyDuids: string[]) => void;
  $updateLayoutsRemapDartboardFiltersNoBackend: (dartboardDuidRemap: Map<string, string>) => LayoutUpdate[];
  $replicateLayoutsForDartboardNoBackend: (
    oldDartboardDuid: string,
    newDartboardDuid: string
  ) => Promise<{
    layoutUpdates: LayoutUpdate[];
    layoutCreates: Layout[];
    userDartboardLayouts: UserDartboardLayout[];
    userDartboardLayoutCreates: UserDartboardLayoutCreate[];
  }>;
  $setLayouts: (layouts: Layout[], preservationMode: StoreDataPreservationMode) => void;
  $createOrUpdateLayout: (layout: Layout) => void;
  $deleteLayout: (layout: Layout) => void;
  createDashboard: (
    order: string,
    partialDashboard?: Partial<Dashboard>,
    partialLayout?: Partial<Layout>,
    options?: { awaitBackend?: boolean }
  ) => Promise<Dashboard>;
  updateDashboard: (
    update: DashboardUpdate,
    options?: { awaitBackend?: boolean; noBackend?: boolean }
  ) => Promise<Dashboard | undefined>;
  updateDashboardAccessors: (
    dashboardDuid: string,
    accessorDuids: string[],
    add?: boolean,
    options?: { awaitBackend?: boolean }
  ) => Promise<Dashboard | undefined>;
  updateDashboardFavorite: (
    dashboardDuid: string,
    favorite: boolean,
    options?: { awaitBackend?: boolean }
  ) => Promise<Dashboard | undefined>;
  replicateDashboard: (
    dashboard: Dashboard,
    order: string,
    partialDashboard?: Partial<Dashboard>,
    options?: { awaitBackend?: boolean; maintainTitle?: boolean }
  ) => Promise<Dashboard | undefined>;
  deleteDashboard: (dashboard: Dashboard, options?: { awaitBackend?: boolean }) => Promise<void>;
  $setDashboards: (dashboards: Dashboard[]) => void;
  $createOrUpdateDashboard: (dashboard: Dashboard) => void;
  $deleteDashboard: (dashboard: Dashboard) => void;
};

const getters: PiniaGetterAdaptor<Getters, DataStore> = {
  viewList() {
    return this._sortedViewDuids.map((e) => this._duidsToViews.get(e)).filter(isDefined);
  },
  getViewByDuid() {
    return (duid) => this._duidsToViews.get(duid);
  },
  getViewByKind() {
    return (kind) => {
      const view = this.viewList.filter((e) => e.kind === kind);
      if (view.length !== 1) {
        throw new Error(`Expected exactly one view of kind ${kind}`);
      }
      return view[0];
    };
  },
  layoutList() {
    return this._sortedLayoutDuids.map((e) => this._duidsToLayouts.get(e)).filter(isDefined);
  },
  getLayoutByDuid() {
    return (duid) => this._duidsToLayouts.get(duid);
  },
  getLayoutByDartboardDuid() {
    return (dartboardDuid) => {
      const dartboard = this.getDartboardByDuid(dartboardDuid);
      if (!dartboard) {
        return undefined;
      }
      const userDuid = this.$useUserStore().duid;
      const duid = dartboard.userDuidsToLayoutDuids.find((e) => e.userDuid === userDuid)?.layoutDuid;
      if (!duid) {
        return undefined;
      }
      return this.getLayoutByDuid(duid);
    };
  },
  dashboardList() {
    return this._sortedDashboardDuids.map((e) => this._duidsToDashboards.get(e)).filter(isDefined);
  },
  getDashboardByDuid() {
    return (duid) => this._duidsToDashboards.get(duid);
  },
};

const actions: PiniaActionAdaptor<Actions, DataStore> = {
  async createView(order, partialView, partialLayout, options = {}) {
    const { awaitBackend = false } = options;
    const appStore = this.$useAppStore();
    const userStore = this.$useUserStore();

    const newLayout = this.makeLayoutNoBackend(partialLayout);

    const newView: View = {
      kind: ViewKind.CUSTOM,
      accessibleByTeam: false,
      accessibleByUserDuids: [userStore.duid],
      public: false,
      title: "",
      description: "",
      iconKind: IconKind.NONE,
      iconNameOrEmoji: randomSample(appStore.pageIconOptions)[0].name,
      colorHex: makeRandomColorHex(),
      favoritedByUserDuids: [],
      alwaysShownPropertyDuids: [],
      alwaysHiddenPropertyDuids: [],
      propertyOrderDuids: [],
      propertyWidthMap: {},
      ...partialView,
      duid: makeDuid(),
      order,
      layoutDuid: newLayout.duid,
      pageKind: PageKind.VIEW,
    };
    this.$createOrUpdateView(newView);

    const backendAction = this.$backend.view.create(newView, newLayout);
    if (awaitBackend) {
      await backendAction;
    }
    return newView;
  },
  async updateView(update, options = {}) {
    const { awaitBackend = false, noBackend = false } = options;
    const view = this.getViewByDuid(update.duid);
    if (!view) {
      return undefined;
    }

    Object.assign(view, update);
    updateInSorted(this._duidsToViews, this._sortedViewDuids, view.duid, viewComparator);

    if (this.$usePageStore().isPublicView) {
      return view;
    }

    if (noBackend) {
      return view;
    }

    const backendAction = this.$backend.view.update(update);
    if (awaitBackend) {
      await backendAction;
    }

    return view;
  },
  async updateViewAccessors(duid, accessorDuids, add, options = {}) {
    const { awaitBackend = false } = options;
    const view = this.getViewByDuid(duid);
    if (!view) {
      return undefined;
    }

    let backendAction: Promise<TransactionResponse>;

    if (add) {
      view.accessibleByUserDuids.push(...accessorDuids);
      backendAction = this.$backend.view.updateListAdd({
        duid: view.duid,
        accessibleByUserDuids: accessorDuids,
      });
    } else {
      filterInPlace(view.accessibleByUserDuids, (e) => !accessorDuids.includes(e));
      backendAction = this.$backend.view.updateListRemove({
        duid: view.duid,
        accessibleByUserDuids: accessorDuids,
      });
    }

    if (awaitBackend) {
      await backendAction;
    }
    updateInSorted(this._duidsToViews, this._sortedViewDuids, view.duid, viewComparator);

    return view;
  },
  async updateViewFavorite(viewDuid, favorite, options = {}) {
    const { awaitBackend = false } = options;
    const view = this.getViewByDuid(viewDuid);
    if (!view) {
      return undefined;
    }

    const userStore = this.$useUserStore();
    let backendAction: Promise<TransactionResponse>;

    if (favorite) {
      view.favoritedByUserDuids.push(userStore.duid);
      backendAction = this.$backend.view.updateListAdd({
        duid: view.duid,
        favoritedByUserDuids: [userStore.duid],
      });
    } else {
      filterInPlace(view.favoritedByUserDuids, (e) => e !== userStore.duid);
      backendAction = this.$backend.view.updateListRemove({
        duid: view.duid,
        favoritedByUserDuids: [userStore.duid],
      });
    }

    if (awaitBackend) {
      await backendAction;
    }
    updateInSorted(this._duidsToViews, this._sortedViewDuids, view.duid, viewComparator);

    return view;
  },
  async replicateView(view, order, partialView, options = {}) {
    const { awaitBackend = false, maintainTitle = false } = options;
    const appStore = this.$useAppStore();

    const layout = this.getLayoutByDuid(view.layoutDuid);
    if (!layout) {
      return undefined;
    }

    const newLayout = {
      ...deepCopy(layout),
      duid: makeDuid(),
    };

    this.$createOrUpdateLayout(newLayout);

    const newView: View = {
      ...deepCopy(view),
      duid: makeDuid(),
      kind: ViewKind.CUSTOM,
      order,
      layoutDuid: newLayout.duid,
      ...partialView,
    };

    if (newView.public) {
      const tenantStore = this.$useTenantStore();
      const maxPublicViews = tenantStore.getEntitlementValue(Entitlement.MAX_PUBLIC_VIEWS);
      const currentPublicViewCount = this.viewList.filter((e) => e.public).length;
      if (currentPublicViewCount >= maxPublicViews) {
        newView.public = false;
        appStore.setUpgradeConfirmationModalOpen({
          featureAction: "create more public views",
          subscription: Subscription.BUSINESS,
        });
      }
    }
    if (!maintainTitle && partialView?.title === undefined) {
      newView.title = getNextTitleInSequence(
        view.title,
        this.viewList.map((e) => e.title)
      );
    }

    // TODO do a better icon here
    if (view.kind === ViewKind.MY_TASKS) {
      newView.iconKind = IconKind.ICON;
      newView.iconNameOrEmoji = randomSample(appStore.pageIconOptions)[0].name;
    }

    this.$createOrUpdateView(newView);

    const backendAction = this.$backend.view.create(newView, newLayout);
    if (awaitBackend) {
      await backendAction;
    }

    return newView;
  },
  async deleteView(view, options = {}) {
    const { awaitBackend = false } = options;
    this.$deleteView(view);

    const backendAction = this.$backend.view.delete(view.duid);

    if (awaitBackend) {
      await backendAction;
    }
  },
  $setViews(views) {
    setObjectMapAndSorted(
      this._duidsToViews,
      this._sortedViewDuids,
      views.map((e) => ({ ...e, pageKind: PageKind.VIEW }) satisfies View),
      viewComparator
    );
  },
  $createOrUpdateView(view) {
    /* Shared */
    const currentView = this.getViewByDuid(view.duid);

    // TODO remove this check when we are filtering out spurious updates on BE of WS
    if (
      !(
        (this.$useUserStore().isRoleGte(UserRole.MEMBER) && view.accessibleByTeam) ||
        view.accessibleByUserDuids.includes(this.$useUserStore().duid)
      )
    ) {
      if (currentView) {
        this.$deleteView(view);
      }
      return;
    }

    // eslint-disable-next-line no-param-reassign
    view.pageKind = PageKind.VIEW;

    if (!currentView) {
      this._duidsToViews.set(view.duid, view);
    } else {
      Object.assign(currentView, view);
    }
    updateInSorted(this._duidsToViews, this._sortedViewDuids, view.duid, viewComparator);
  },
  $deleteView(view) {
    const deletingCurrentPage = view.duid === this.$useAppStore().currentPage?.duid;

    this._duidsToViews.delete(view.duid);
    removeFromSorted(this._sortedViewDuids, view.duid);

    if (deletingCurrentPage) {
      this.$routerUtils.goHome();
    }
  },
  async updatePage(pagePartial, pageKind, options = {}) {
    switch (pageKind) {
      case PageKind.DARTBOARD: {
        await this.updateDartboard(pagePartial as DartboardUpdate, options);
        break;
      }
      case PageKind.DASHBOARD: {
        await this.updateDashboard(pagePartial as DashboardUpdate, options);
        break;
      }
      case PageKind.DOC: {
        await this.updateDocs([pagePartial], options);
        break;
      }
      case PageKind.FOLDER: {
        await this.updateFolder(pagePartial as FolderUpdate, options);
        break;
      }
      case PageKind.FORM: {
        await this.updateForm(pagePartial, options);
        break;
      }
      case PageKind.SPACE: {
        await this.updateSpace(pagePartial, options);
        break;
      }
      case PageKind.VIEW: {
        await this.updateView(pagePartial, options);
        break;
      }
      default: {
        throw new Error(`Unknown page kind: ${pageKind}`);
      }
    }
  },
  async updatePageAccessors(page, accessorDuids, add, options = {}) {
    const { pageKind } = page;
    switch (pageKind) {
      case PageKind.SPACE: {
        await this.updateSpaceAccessors(page.duid, accessorDuids, add, options);
        break;
      }
      case PageKind.VIEW: {
        await this.updateViewAccessors(page.duid, accessorDuids, add, options);
        break;
      }
      case PageKind.DASHBOARD: {
        await this.updateDashboardAccessors(page.duid, accessorDuids, add, options);
        break;
      }
      default: {
        throw new Error(`Unknown page kind: ${pageKind}`);
      }
    }
  },
  async replicatePage(page, options = {}) {
    const { pageKind } = page;
    // TODO get a better order here
    const order = getOrdersBetween(undefined, page.order)[0];
    switch (pageKind) {
      case PageKind.DARTBOARD: {
        await this.replicateDartboard(page, order, {}, options);
        break;
      }
      case PageKind.DOC: {
        await this.replicateDoc(page, order, {}, options);
        break;
      }
      case PageKind.FOLDER: {
        await this.replicateFolder(page, order, {}, options);
        break;
      }
      case PageKind.SPACE: {
        await this.replicateSpace(page, order, {}, options);
        break;
      }
      case PageKind.VIEW: {
        await this.replicateView(page, order, {}, options);
        break;
      }
      case PageKind.DASHBOARD: {
        await this.replicateDashboard(page, order, {}, options);
        break;
      }
      default: {
        throw new Error(`Unknown page kind: ${pageKind}`);
      }
    }
  },
  async deletePage(page, options = {}) {
    const { pageKind } = page;
    switch (pageKind) {
      case PageKind.DARTBOARD: {
        this.deleteDartboard(page, options);
        break;
      }
      case PageKind.DOC: {
        await this.trashDoc(page);
        break;
      }
      case PageKind.FOLDER: {
        await this.deleteFolder(page, options);
        break;
      }
      case PageKind.SPACE: {
        this.deleteSpace(page);
        break;
      }
      case PageKind.VIEW: {
        this.deleteView(page, options);
        break;
      }
      case PageKind.DASHBOARD: {
        this.deleteDashboard(page, options);
        break;
      }
      default: {
        throw new Error(`Unknown page kind: ${pageKind}`);
      }
    }
  },
  makeLayoutNoBackend(partialLayout) {
    const now = new Date().toISOString();
    const statusPropertyDuid = this.defaultStatusProperty.duid;

    const newLayout: Layout = {
      kind: LayoutKind.LIST,
      createdAt: now,
      updatedAt: now,
      filterGroup: {
        filters: [],
        connector: FilterConnector.AND,
      },
      sorts: [],
      kindConfigMap: {
        [LayoutKind.LIST]: {
          subtaskDisplayMode: SubtaskDisplayMode.INDENTED,
          showAbsentees: true,
          groupBy: getDefaultListGroupBy(),
          collapsedGroups: [],
          hideEmptyGroups: false,
        },
        [LayoutKind.BOARD]: {
          subtaskDisplayMode: SubtaskDisplayMode.INDENTED,
          showAbsentees: true,
          groupBy: getDefaultBoardGroupBy(),
          subgroupBy: UNSET_PSEUDO_SUBGROUP_BY,
          collapsedGroups: [],
          hideEmptyGroups: false,
        },
        [LayoutKind.CALENDAR]: {
          subtaskDisplayMode: SubtaskDisplayMode.INDENTED,
          showAbsentees: true,
          displayPeriod: "month",
          displayPeriodCount: 1,
          colorBy: statusPropertyDuid,
        },
        [LayoutKind.ROADMAP]: {
          subtaskDisplayMode: SubtaskDisplayMode.INDENTED,
          showAbsentees: true,
          pxPerDay: 10,
          taskListWidthPx: 550,
          colorBy: statusPropertyDuid,
        },
        [LayoutKind.WORKFLOW]: {
          subtaskDisplayMode: SubtaskDisplayMode.INDENTED,
          showAbsentees: true,
        },
      },
      summaryStatisticKind: SummaryStatisticKind.TOTAL_COUNT,
      duid: makeDuid(),
      ...partialLayout,
    };

    this.$createOrUpdateLayout(newLayout);

    return newLayout;
  },
  async createLayoutForDartboard(dartboardDuid, filters, options = {}) {
    const { awaitBackend = false } = options;
    const dartboard = this.getDartboardByDuid(dartboardDuid);
    if (!dartboard) {
      return undefined;
    }

    const userDuid = this.$useUserStore().duid;
    const newLayout = this.makeLayoutNoBackend({ filterGroup: { filters, connector: FilterConnector.AND } });
    dartboard.userDuidsToLayoutDuids.push({
      userDuid,
      layoutDuid: newLayout.duid,
    });

    const userDartboardLayout = {
      userDuid,
      dartboardDuid,
      layoutDuid: newLayout.duid,
    };
    const backendAction = this.$backend.layout.create(newLayout, userDartboardLayout);
    if (awaitBackend) {
      await backendAction;
    }

    return newLayout;
  },
  async updateLayout(update, options = {}) {
    const { noBackend = false, awaitBackend = false } = options;

    const layout = this.getLayoutByDuid(update.duid);
    if (!layout) {
      return undefined;
    }

    Object.assign(layout, update);
    layout.updatedAt = new Date().toISOString();

    let backendAction: Promise<TransactionResponse> | undefined;
    if (!noBackend && !this.$usePageStore().isPublicView) {
      backendAction = this.$backend.layout.update(update);
    }

    if (awaitBackend && backendAction) {
      await backendAction;
    }

    return layout;
  },
  $cleanupLayoutsFiltersNoBackend(entityDuids, propertyDuids, { removeAllFilters = false } = {}) {
    const propertyDuidSet = new Set(propertyDuids);

    const layoutUpdates: LayoutUpdate[] = [];

    // Go through the filter groups and remove the entity duid
    this.layoutList.forEach((layout) => {
      // Use a deep copy because we don't want to modify the original layout just yet
      const updatedFilterGroup: FilterGroup = deepCopy(layout.filterGroup);

      let changed = false;
      const indicesOfFiltersToRemove: number[] = [];

      // We're only removing the entity duid from the filter values
      updatedFilterGroup.filters.forEach((filter, filterIndex) => {
        if (!propertyDuidSet.has(filter.propertyDuid)) {
          return;
        }
        const indicesOfEntityDuids = entityDuids
          .map((entityDuid) => filter.values.indexOf(entityDuid))
          .filter((index) => index !== -1);

        indicesOfEntityDuids.sort((a, b) => b - a); // Sort in desc to avoid index shifting issues when removing items

        indicesOfEntityDuids.forEach((index) => {
          filter.values.splice(index, 1);
          changed = true;
        });
        // if the filter is empty, remove it - or else it causes a weird visual bug
        if (filter.values.length === 0 || removeAllFilters) {
          indicesOfFiltersToRemove.push(filterIndex);
        }
      });

      if (indicesOfFiltersToRemove.length > 0) {
        indicesOfFiltersToRemove.sort((a, b) => b - a);

        indicesOfFiltersToRemove.forEach((index) => {
          updatedFilterGroup.filters.splice(index, 1);
        });
        changed = true;
      }

      if (changed) {
        layoutUpdates.push({
          duid: layout.duid,
          filterGroup: updatedFilterGroup,
        });
      }
    });

    layoutUpdates.forEach((update) => this.updateLayout(update, { noBackend: true }));
  },
  $cleanupLayoutsKindConfigMapNoBackend(propertyDuids) {
    const propertyDuidsSet = new Set(propertyDuids);

    const DEFAULTS: Record<LayoutKind, Partial<LayoutConfig>> = {
      [LayoutKind.LIST]: { groupBy: UNGROUPED_PSEUDO_GROUP_BY },
      [LayoutKind.BOARD]: { groupBy: this.defaultStatusProperty.duid, subgroupBy: UNSET_PSEUDO_SUBGROUP_BY },
      [LayoutKind.CALENDAR]: { colorBy: UNCOLORED_PSEUDO_COLOR_BY },
      [LayoutKind.ROADMAP]: { colorBy: UNCOLORED_PSEUDO_COLOR_BY },
      [LayoutKind.WORKFLOW]: {},
    };

    const layoutUpdates = this.layoutList.map((layout) => {
      const updatedKindConfigMap = deepCopy(layout.kindConfigMap);

      Object.entries(updatedKindConfigMap).forEach(([kindKey, config]) => {
        const kind = kindKey as LayoutKind;
        const defaults = DEFAULTS[kind] ?? {};

        (["groupBy", "subgroupBy", "colorBy"] as const).forEach((field) => {
          if (!(field in config)) {
            return;
          }

          const configRecord = config;
          const fieldValue = configRecord[field as keyof LayoutConfig];
          if (!isString(fieldValue)) {
            return;
          }

          const defaultValue = defaults[field as keyof LayoutConfig];
          if (propertyDuidsSet.has(fieldValue) && defaultValue) {
            (updatedKindConfigMap[kind] as Record<string, unknown>)[field] = defaultValue;
          }
        });
      });

      return { duid: layout.duid, kindConfigMap: updatedKindConfigMap };
    });

    layoutUpdates.forEach((update) => this.updateLayout(update, { noBackend: true }));
  },
  $cleanupViewsPropertyReferencesNoBackend(propertyDuids) {
    const propertyDuidSet = new Set(propertyDuids);

    const viewUpdates = this.viewList.map((view) => ({
      duid: view.duid,
      alwaysShownPropertyDuids: view.alwaysShownPropertyDuids.filter((duid) => !propertyDuidSet.has(duid)),
      alwaysHiddenPropertyDuids: view.alwaysHiddenPropertyDuids.filter((duid) => !propertyDuidSet.has(duid)),
      propertyOrderDuids: view.propertyOrderDuids.filter((duid) => !propertyDuidSet.has(duid)),
      propertyWidthMap: Object.fromEntries(
        Object.entries(view.propertyWidthMap).filter(([duid]) => !propertyDuidSet.has(duid))
      ),
    }));

    viewUpdates.forEach((update) => this.updateView(update, { noBackend: true }));
  },
  $cleanupLayoutsPropertySortsNoBackend(propertyDuids) {
    const propertyDuidSet = new Set(propertyDuids);

    const layoutUpdates = this.layoutList
      .filter((layout) => layout.sorts && layout.sorts.length > 0)
      .map((layout) => ({
        duid: layout.duid,
        sorts: layout.sorts.filter((sort) => !propertyDuidSet.has(sort.propertyDuid)),
      }));

    layoutUpdates.forEach((update) => this.updateLayout(update, { noBackend: true }));
  },
  $updateLayoutsRemapDartboardFiltersNoBackend(dartboardDuidRemap) {
    const layoutUpdates: LayoutUpdate[] = [];

    // Go through the filter groups and update the dartboard duid
    const dartboardPropertyDuid = this.defaultDartboardProperty.duid;
    this.layoutList.forEach((layout) => {
      // Use a deep copy because we don't want to modify the original layout just yet
      const updatedFilterGroup = deepCopy(layout.filterGroup);

      let changed = false;
      updatedFilterGroup.filters
        .filter((e) => e.propertyDuid === dartboardPropertyDuid)
        .forEach((filter) => {
          filter.values.forEach((item, index) => {
            if (!isString(item)) {
              return;
            }
            const newValue = dartboardDuidRemap.get(item);
            if (!newValue) {
              return;
            }
            // eslint-disable-next-line no-param-reassign
            filter.values[index] = newValue;
            changed = true;
          });
        });

      if (changed) {
        layoutUpdates.push({
          duid: layout.duid,
          filterGroup: updatedFilterGroup,
        });
      }
    });

    layoutUpdates.forEach((update) => this.updateLayout(update, { noBackend: true }));
    return layoutUpdates;
  },
  async $replicateLayoutsForDartboardNoBackend(oldDartboardDuid, newDartboardDuid) {
    const res = {
      layoutUpdates: [] as LayoutUpdate[],
      layoutCreates: [] as Layout[],
      userDartboardLayouts: [] as UserDartboardLayout[],
      userDartboardLayoutCreates: [] as UserDartboardLayoutCreate[],
    };

    const oldDartboard = this.getDartboardByDuid(oldDartboardDuid);
    const newDartboard = this.getDartboardByDuid(newDartboardDuid);
    if (!oldDartboard) {
      return res;
    }

    // Create a map of user duids to layout duids for the new dartboard
    const newDartboardUserDuidToLayoutDuids = new Map(
      (newDartboard?.userDuidsToLayoutDuids ?? []).map((e) => [e.userDuid, e.layoutDuid])
    );
    const layoutPromises = oldDartboard.userDuidsToLayoutDuids.map(async (userDartboardLayout) => {
      const { userDuid, layoutDuid } = userDartboardLayout;
      const oldLayout = this.getLayoutByDuid(layoutDuid);
      if (!oldLayout) {
        return;
      }

      const oldLayoutWithoutDuid = deepCopy(oldLayout) as Partial<Layout>;
      delete oldLayoutWithoutDuid.duid;

      // If the user already has a layout for the new dartboard update the layout
      const existingLayoutDuid = newDartboardUserDuidToLayoutDuids.get(userDuid);
      if (existingLayoutDuid) {
        const updatedLayout = await this.updateLayout(
          { ...oldLayoutWithoutDuid, duid: existingLayoutDuid },
          { noBackend: true }
        );
        if (!updatedLayout) {
          return;
        }

        res.layoutUpdates.push(updatedLayout);
        return;
      }

      // Otherwise create a new layout for the user
      const newLayout = this.makeLayoutNoBackend(oldLayoutWithoutDuid);
      res.layoutCreates.push(newLayout);

      res.userDartboardLayouts.push({
        userDuid,
        layoutDuid: newLayout.duid,
      });

      res.userDartboardLayoutCreates.push({
        userDuid,
        dartboardDuid: newDartboardDuid,
        layoutDuid: newLayout.duid,
      });
    });

    const updatedUserDuids = new Set(oldDartboard.userDuidsToLayoutDuids.map((e) => e.userDuid));
    newDartboard?.userDuidsToLayoutDuids
      ?.filter((e) => !updatedUserDuids.has(e.userDuid))
      ?.forEach(async (userDartboardLayout) => {
        const updatedLayout = await this.updateLayout(
          this.makeLayoutNoBackend({ duid: userDartboardLayout.layoutDuid }),
          { noBackend: true }
        );
        if (!updatedLayout) {
          return;
        }

        res.layoutUpdates.push(updatedLayout);
      });

    await Promise.all(layoutPromises);

    return res;
  },
  $setLayouts(layouts, preservationMode) {
    const recentCutoff = moment().subtract(30, "second");

    const newLayouts: Layout[] = [];
    layouts.forEach((newLayout) => {
      const oldLayout = this.getLayoutByDuid(newLayout.duid);
      if (!oldLayout) {
        newLayouts.push(newLayout);
        return;
      }
      if (preservationMode === StoreDataPreservationMode.NONE) {
        Object.assign(oldLayout, newLayout);
        newLayouts.push(oldLayout);
        return;
      }
      if (preservationMode === StoreDataPreservationMode.ALL) {
        newLayouts.push(oldLayout);
        return;
      }
      if (moment(oldLayout.updatedAt).isAfter(recentCutoff)) {
        newLayouts.push(oldLayout);
        return;
      }
      Object.assign(oldLayout, newLayout);
      newLayouts.push(oldLayout);
    });
    const newLayoutDuids = new Set(newLayouts.map((e) => e.duid));
    newLayouts.push(
      ...[...this._duidsToLayouts.values()].filter(
        (e) => !newLayoutDuids.has(e.duid) && moment(e.createdAt).isAfter(recentCutoff)
      )
    );
    setObjectMapAndSorted(this._duidsToLayouts, this._sortedLayoutDuids, newLayouts, layoutComparator);
  },
  $createOrUpdateLayout(layout) {
    const currentLayout = this.getLayoutByDuid(layout.duid);
    if (!currentLayout) {
      // TODO handle bleeding of hidden views' layouts into the store
      this._duidsToLayouts.set(layout.duid, layout);
    } else {
      Object.assign(currentLayout, layout);
    }
    updateInSorted(this._duidsToLayouts, this._sortedLayoutDuids, layout.duid, layoutComparator);
  },
  $deleteLayout(layout) {
    this._duidsToLayouts.delete(layout.duid);
    removeFromSorted(this._sortedLayoutDuids, layout.duid);
  },
  async createDashboard(order, partialDashboard, partialLayout, options = {}) {
    const { awaitBackend = false } = options;
    const appStore = this.$useAppStore();
    const userStore = this.$useUserStore();

    const newLayout = this.makeLayoutNoBackend(partialLayout);

    const newDashboard: Dashboard = {
      kind: DashboardKind.DASHBOARD,
      accessibleByTeam: false,
      accessibleByUserDuids: [userStore.duid],
      title: "",
      description: "",
      iconKind: IconKind.NONE,
      iconNameOrEmoji: randomSample(appStore.pageIconOptions)[0].name,
      colorHex: makeRandomColorHex(),
      favoritedByUserDuids: [],
      charts: [],
      ...partialDashboard,
      duid: makeDuid(),
      order,
      layoutDuid: newLayout.duid,
      pageKind: PageKind.DASHBOARD,
    };
    this.$createOrUpdateDashboard(newDashboard);

    const backendAction = this.$backend.dashboard.create(newDashboard, newLayout);
    if (awaitBackend) {
      await backendAction;
    }
    return newDashboard;
  },
  async updateDashboard(update, options = {}) {
    const { awaitBackend = false, noBackend = false } = options;
    const dashboard = this.getDashboardByDuid(update.duid);
    if (!dashboard) {
      return undefined;
    }

    Object.assign(dashboard, update);
    updateInSorted(this._duidsToDashboards, this._sortedDashboardDuids, dashboard.duid, dashboardComparator);

    if (noBackend) {
      return dashboard;
    }

    const backendAction = this.$backend.dashboard.update(update);
    if (awaitBackend) {
      await backendAction;
    }

    return dashboard;
  },
  async updateDashboardAccessors(duid, accessorDuids, add, options = {}) {
    const { awaitBackend = false } = options;
    const dashboard = this.getDashboardByDuid(duid);
    if (!dashboard) {
      return undefined;
    }

    let backendAction: Promise<TransactionResponse>;

    if (add) {
      dashboard.accessibleByUserDuids.push(...accessorDuids);
      backendAction = this.$backend.dashboard.updateListAdd({
        duid: dashboard.duid,
        accessibleByUserDuids: accessorDuids,
      });
    } else {
      filterInPlace(dashboard.accessibleByUserDuids, (e) => !accessorDuids.includes(e));
      backendAction = this.$backend.dashboard.updateListRemove({
        duid: dashboard.duid,
        accessibleByUserDuids: accessorDuids,
      });
    }

    if (awaitBackend) {
      await backendAction;
    }
    updateInSorted(this._duidsToDashboards, this._sortedDashboardDuids, dashboard.duid, dashboardComparator);

    return dashboard;
  },
  async updateDashboardFavorite(dashboardDuid, favorite, options = {}) {
    const { awaitBackend = false } = options;
    const dashboard = this.getDashboardByDuid(dashboardDuid);
    if (!dashboard) {
      return undefined;
    }

    const userStore = this.$useUserStore();
    let backendAction: Promise<TransactionResponse>;

    if (favorite) {
      dashboard.favoritedByUserDuids.push(userStore.duid);
      backendAction = this.$backend.dashboard.updateListAdd({
        duid: dashboard.duid,
        favoritedByUserDuids: [userStore.duid],
      });
    } else {
      filterInPlace(dashboard.favoritedByUserDuids, (e) => e !== userStore.duid);
      backendAction = this.$backend.dashboard.updateListRemove({
        duid: dashboard.duid,
        favoritedByUserDuids: [userStore.duid],
      });
    }

    if (awaitBackend) {
      await backendAction;
    }
    updateInSorted(this._duidsToDashboards, this._sortedDashboardDuids, dashboard.duid, dashboardComparator);

    return dashboard;
  },
  async replicateDashboard(dashboard, order, partialDashboard, options = {}) {
    const { awaitBackend = false, maintainTitle = false } = options;

    const tenantStore = this.$useTenantStore();
    const maxDashboards = tenantStore.getEntitlementValue(Entitlement.MAX_DASHBOARDS);
    const currentDashboardCount = this.dashboardList.length;
    if (currentDashboardCount >= maxDashboards) {
      const appStore = this.$useAppStore();
      appStore.setUpgradeConfirmationModalOpen({
        featureAction: "use dashboards",
        subscription: Subscription.BUSINESS,
      });
      return undefined;
    }

    const oldLayout = this.getLayoutByDuid(dashboard.layoutDuid);
    if (!oldLayout) {
      return undefined;
    }
    const newLayout = {
      ...deepCopy(oldLayout),
      duid: makeDuid(),
    };
    this.$createOrUpdateLayout(newLayout);

    const newDashboard: Dashboard = {
      ...deepCopy(dashboard),
      duid: makeDuid(),
      kind: DashboardKind.DASHBOARD,
      order,
      layoutDuid: newLayout.duid,
      ...partialDashboard,
    };
    if (!maintainTitle && partialDashboard?.title === undefined) {
      newDashboard.title = getNextTitleInSequence(
        dashboard.title,
        this.dashboardList.map((e) => e.title)
      );
    }

    this.$createOrUpdateDashboard(newDashboard);

    const backendAction = this.$backend.dashboard.create(newDashboard, newLayout);
    if (awaitBackend) {
      await backendAction;
    }

    return newDashboard;
  },
  async deleteDashboard(dashboard, options = {}) {
    const { awaitBackend = false } = options;
    this.$deleteDashboard(dashboard);

    const backendAction = this.$backend.dashboard.delete(dashboard.duid);

    if (awaitBackend) {
      await backendAction;
    }
  },
  $setDashboards(dashboards) {
    setObjectMapAndSorted(
      this._duidsToDashboards,
      this._sortedDashboardDuids,
      dashboards.map(
        (e) => ({ ...e, pageKind: PageKind.DASHBOARD, kind: DashboardKind.DASHBOARD }) satisfies Dashboard
      ),
      dashboardComparator
    );
  },
  $createOrUpdateDashboard(dashboard) {
    /* Shared */
    const currentDashboard = this.getDashboardByDuid(dashboard.duid);

    // TODO remove this check when we are filtering out spurious updates on BE of WS
    if (
      !(
        (this.$useUserStore().isRoleGte(UserRole.MEMBER) && dashboard.accessibleByTeam) ||
        dashboard.accessibleByUserDuids.includes(this.$useUserStore().duid)
      )
    ) {
      if (currentDashboard) {
        this.$deleteDashboard(dashboard);
      }
      return;
    }

    // eslint-disable-next-line no-param-reassign
    dashboard.pageKind = PageKind.DASHBOARD;
    // eslint-disable-next-line no-param-reassign
    dashboard.kind = DashboardKind.DASHBOARD;

    if (!currentDashboard) {
      this._duidsToDashboards.set(dashboard.duid, dashboard);
    } else {
      Object.assign(currentDashboard, dashboard);
    }
    updateInSorted(this._duidsToDashboards, this._sortedDashboardDuids, dashboard.duid, dashboardComparator);
  },
  $deleteDashboard(dashboard) {
    const deletingCurrentPage = dashboard.duid === this.$useAppStore().currentPage?.duid;

    this._duidsToDashboards.delete(dashboard.duid);
    removeFromSorted(this._sortedDashboardDuids, dashboard.duid);

    if (deletingCurrentPage) {
      this.$routerUtils.goHome();
    }
  },
};

export { actions, getters };
